import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useApiClient from '../../../hooks/api/useApiClient';

/**
 * The restaurant's tables and every change to them. Each change saves right away and confirms with
 * a toast; a refused name comes back as the message to show under the field. A list that didn't
 * load says so (`failed`), so it never reads as a restaurant without tables; `reload` tries again.
 *
 * `showsMigratedNote`: "Set up automatically" shows while the backend says so on the tables
 * (`shows_migrated_note`, true until someone adds, renames, switches, deletes or reorders a table),
 * and goes right after any such change saved here, before the list is loaded again.
 */
export default function useOnSiteTables(companyId) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Takeout:Tables:TableList' });
  const { OnSiteTablesManager } = useApiClient();

  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [changedHere, setChangedHere] = useState(false);
  const tablesRef = useRef(tables);
  tablesRef.current = tables;
  // Reorders go out one at a time, so the server saves them in the order they were made: moves
  // made while one is out are sent as one order when it returns. `saved` is the last order the
  // server has, to fall back to when a save fails.
  const reorderQueue = useRef({ sending: false, next: null, saved: null });

  useEffect(() => {
    let current = true;

    setLoading(true);
    setFailed(false);
    setChangedHere(false);
    OnSiteTablesManager.get().then(({ success, data }) => {
      if (!current) {
        return;
      }

      setLoading(false);

      if (!success) {
        setTables([]);
        setFailed(true);

        return;
      }

      setTables(Array.isArray(data) ? data : []);
    });

    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, attempt]);

  const reload = useCallback(() => setAttempt((count) => count + 1), []);

  const changed = useCallback(() => setChangedHere(true), []);

  /** Resolves to null when added, or the message to show under the field. */
  const add = async (name) => {
    const { success, data } = await OnSiteTablesManager.create({ name });

    if (!success) {
      return nameError(data) ?? t('toasts.failed_to_save');
    }

    setTables((list) => [...list, data]);
    changed();
    toast.success(t('toasts.added', { name: data.name }));

    return null;
  };

  /** Resolves to null when renamed, or the message to show under the field. */
  const rename = async (table, name) => {
    const { success, data } = await OnSiteTablesManager.update(table.id, { name });

    if (!success) {
      return nameError(data) ?? t('toasts.failed_to_save');
    }

    setTables((list) => list.map((item) => (item.id === table.id ? { ...item, ...data } : item)));
    changed();

    if (data.name !== table.name) {
      toast.success(t('toasts.renamed', { old: table.name, name: data.name }));
    }

    return null;
  };

  const setActive = async (table, isActive) => {
    setTables((list) => list.map((item) => (item.id === table.id ? { ...item, is_active: isActive } : item)));
    const { success } = await OnSiteTablesManager.update(table.id, { is_active: isActive });

    if (!success) {
      setTables((list) => list.map((item) => (item.id === table.id ? { ...item, is_active: table.is_active } : item)));
      toast.error(t('toasts.failed_to_save'));

      return;
    }

    changed();
    toast.success(t(isActive ? 'toasts.switched_on' : 'toasts.switched_off', { name: table.name }));
  };

  /** Resolves to whether the table was deleted. */
  const remove = async (table) => {
    const { success } = await OnSiteTablesManager.delete(table.id);

    if (!success) {
      toast.error(t('toasts.failed_to_save'));

      return false;
    }

    setTables((list) => list.filter((item) => item.id !== table.id));
    changed();
    toast.success(t('toasts.deleted', { name: table.name }));

    return true;
  };

  const sendOrder = async () => {
    const queue = reorderQueue.current;
    // A table deleted while a move waited is left out: the server refuses an order with it.
    const present = new Set(tablesRef.current.map((table) => table.id));
    const ids = queue.next.filter((id) => present.has(id));
    queue.next = null;
    queue.sending = true;
    const { success } = await OnSiteTablesManager.reorder(ids);

    if (success) {
      queue.saved = ids;
      changed();
    } else {
      // Said for every failed save, also one followed by a queued move (which then sends the
      // whole order again).
      toast.error(t('toasts.failed_to_reorder'));
    }

    if (queue.next) {
      sendOrder();

      return;
    }

    queue.sending = false;

    if (!success) {
      // Only the order goes back; tables added or switched meanwhile stay as they are.
      setTables((list) => sortByIds(list, queue.saved));
    }
  };

  /** Shows the new order right away and puts the last saved order back when the save fails. */
  const reorder = (next) => {
    const queue = reorderQueue.current;

    if (!queue.sending) {
      queue.saved = tablesRef.current.map((table) => table.id);
    }

    queue.next = next.map((table) => table.id);
    setTables((list) => sortByIds(list, queue.next));

    if (!queue.sending) {
      sendOrder();
    }
  };

  const showsMigratedNote = !changedHere && tables.some((table) => table.shows_migrated_note === true);

  return { tables, loading, failed, reload, showsMigratedNote, add, rename, setActive, remove, reorder };
}

/**
 * What the server says about the name (a 422 on `name`), or null. Any other failure (a 500, a 403)
 * isn't about the name, so the field shows the general "didn't save" text instead.
 */
function nameError(data) {
  return data?.errors?.name?.[0] ?? null;
}

/** The list in the order of `ids`; tables not in it (added since) keep their place at the end. */
export function sortByIds(list, ids) {
  const position = new Map((ids ?? []).map((id, index) => [id, index]));

  return [...list].sort((a, b) => (position.get(a.id) ?? Infinity) - (position.get(b.id) ?? Infinity));
}
